import axiosInstance from "axios";
import configuration from "~/configuration";

import { PostUserSigninResponse } from "~/lib/auth/types";

import { API_ENDPOINTS } from "../constants/api-endpoints";
import { routes } from "../constants/routes";
import type {
  BackendApiErrorResponse,
  BackendApiResponse,
} from "../types/backend-api-response.type";

// Create an axios instance for client-side requests
export const axios = axiosInstance.create({
  baseURL: configuration.api.enrichBaseUrl,
  withCredentials: true, // Include cookies in the request
});

// queue to hold requests while refresh is in flight
let isRefreshing = false;
let failedQueues: {
  resolve: (token: string) => void;
  reject: (err: any) => void;
}[] = [];

const processQueues = (err: any, token: string | null = null) => {
  failedQueues.forEach(({ resolve, reject }) => {
    err ? reject(err) : resolve(token!);
  });
  failedQueues = [];
};

axios.interceptors.response.use(
  response => response,
  async err => {
    const { config, response } = err;
    const originalReq = config as typeof config & { _retry?: boolean };

    if (response?.status === 401 && !originalReq._retry) {
      if (isRefreshing) {
        return new Promise<string>((res, rej) => {
          failedQueues.push({ resolve: res, reject: rej });
        }).then(token => {
          originalReq.headers!["Authorization"] = `Bearer ${token}`;
          return axios(originalReq);
        });
      }

      originalReq._retry = true;
      isRefreshing = true;

      return new Promise(async (resolve, reject) => {
        try {
          const { data } = await axios.post<
            BackendApiResponse<PostUserSigninResponse>
          >(API_ENDPOINTS.AUTH.REFRESH_TOKENS);
          const newToken = data?.data?.access_token;

          // axios.defaults.headers!["Authorization"] = `Bearer ${newToken}`;
          processQueues(null, newToken);
          resolve(axios(originalReq));
        } catch (error) {
          await axios.post(API_ENDPOINTS.AUTH.SIGN_OUT);
          window.location.href = routes.auth.signIn;
          processQueues(error, null);
          reject(error);
        } finally {
          isRefreshing = false;
        }
      });
    }

    return Promise.reject(err);
  },
);

// Utility to extract backend API error response
export const getBackendApiErrorResponse = (error: any) => {
  const err = error as { data: BackendApiErrorResponse };
  return err;
};
